import { Button, LegacyCard, Select, Spinner, Text, TextField, Tooltip } from '@shopify/polaris';
import { CancelMinor, DeleteMinor, EditMinor, SaveMinor } from '@shopify/polaris-icons';
import { Fragment, useContext, useState } from 'react';
import { useQuery } from 'react-query';
import {
  deleteProductAlias,
  getProductAliases,
  postProductAlias,
  putProductAlias,
} from '../../../services/productService';
import ToastContext from '../../../context/ToastContext';
import { SET_ERROR, SET_OK } from '../../../context/ToastReducer';
import { checkPermission } from '../../../services/permissionsService';

/**
 * Fila de la tabla de alias.
 *
 * Va a nivel de módulo a propósito: definida dentro del componente se recreaba en
 * cada render, React la trataba como un tipo distinto y remontaba el subárbol, así
 * que los campos perdían el foco a cada tecla.
 *
 * Las dos clases de columnas se escriben enteras (y no interpoladas) porque Tailwind
 * solo genera las que encuentra literales en el fichero.
 */
const Row = ({ canEdit, children, className = '', ...rest }) => (
  <li
    {...rest}
    className={`grid ${
      canEdit ? 'grid-cols-[1fr_1fr_1fr_1fr]' : 'grid-cols-[1fr_1fr_1fr]'
    } gap-4 justify-between border-b p-1 border-gray-200 font-semibold ${className}`}
  >
    {children}
  </li>
);

/**
 * Pestaña "Alias" de la ficha de producto.
 *
 * Un alias redirige un EAN que llega de fuera (normalmente el de EPH, distinto del
 * que MIRTO imprime en la etiqueta) al EAN de la variante viva de esta familia. Se
 * aplica en ventas, folios de traspaso y escaneo, para que la mercancía salga por
 * el mismo código por el que entró.
 */
const ProductAliasesTab = ({ product }) => {
  const [, dispatchToast] = useContext(ToastContext);
  const canEdit = checkPermission('PRODUCT:UPDATE');

  const [alias, setAlias] = useState('');
  const [canonical, setCanonical] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editCanonical, setEditCanonical] = useState('');
  const [editReason, setEditReason] = useState('');

  const {
    data: aliases = [],
    isLoading,
    refetch,
  } = useQuery(['product-aliases', product.id], () => getProductAliases(product.id).then((r) => r.data.data), {
    enabled: !!product.id,
  });

  const variantOptions = [
    { label: '—', value: '' },
    ...product.variants.map((v) => ({ label: `${v.title} · ${v.barcode}`, value: v.barcode })),
  ];

  const titleOf = (barcode) => product.variants.find((v) => v.barcode === barcode)?.title ?? null;

  const fail = (e, fallback) =>
    dispatchToast({ type: SET_ERROR, payload: { message: e?.response?.data?.message || fallback } });

  const resetNew = () => {
    setAlias('');
    setCanonical('');
    setReason('');
  };

  const handleCreate = () => {
    if (!alias.trim() || !canonical) return;
    setSaving(true);
    postProductAlias(product.id, { alias: alias.trim(), canonicalBarcode: canonical, reason: reason.trim() })
      .then(() => {
        dispatchToast({ type: SET_OK, payload: { message: 'Alias creado' } });
        resetNew();
        refetch();
      })
      .catch((e) => fail(e, 'Error al crear el alias'))
      .finally(() => setSaving(false));
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditCanonical(row.canonicalBarcode);
    setEditReason(row.reason || '');
  };

  const handleUpdate = () => {
    setSaving(true);
    putProductAlias(product.id, editingId, { canonicalBarcode: editCanonical, reason: editReason.trim() })
      .then(() => {
        dispatchToast({ type: SET_OK, payload: { message: 'Alias actualizado' } });
        setEditingId(null);
        refetch();
      })
      .catch((e) => fail(e, 'Error al actualizar el alias'))
      .finally(() => setSaving(false));
  };

  const handleDelete = (row) => {
    setSaving(true);
    deleteProductAlias(product.id, row.id)
      .then(() => {
        dispatchToast({ type: SET_OK, payload: { message: 'Alias eliminado' } });
        refetch();
      })
      .catch((e) => fail(e, 'Error al eliminar el alias'))
      .finally(() => setSaving(false));
  };

  return (
    <>
      <LegacyCard.Section>
        <Text variant="bodyMd" tone="subdued">
          Cada alias redirige un código EAN13 arbitrario a su variante correspondiente.
        </Text>
        <ul className="pt-8">
          <Row canEdit={canEdit} className=" font-bold">
            <span>Código que llega</span>
            <span>Se resuelve a</span>
            <span>Motivo</span>
            {canEdit ? <span className="text-right">Acciones</span> : null}
          </Row>
          {isLoading ? (
            <Row canEdit={canEdit}>
              <span className="col-span-4">
                <Spinner accessibilityLabel="Cargando alias" size="small" />
              </span>
            </Row>
          ) : (
            !(aliases.length === 0) &&
            aliases.map((row) => (
              <Fragment key={row.id}>
                <Row canEdit={canEdit} className="font-normal">
                  <span>{row.alias}</span>
                  <span>
                    {editingId === row.id ? (
                      <Select options={variantOptions} value={editCanonical} onChange={setEditCanonical} />
                    ) : (
                      <>
                        {row.canonicalBarcode}
                        {titleOf(row.canonicalBarcode) && (
                          <Text variant="bodySm" tone="subdued">
                            {`talla ${titleOf(row.canonicalBarcode)}`}
                          </Text>
                        )}
                      </>
                    )}
                  </span>
                  <span>
                    {editingId === row.id ? (
                      <TextField label="" labelHidden value={editReason} onChange={setEditReason} autoComplete="off" />
                    ) : (
                      row.reason || '—'
                    )}
                  </span>
                  {canEdit && (
                    <span>
                      <div className="flex justify-end gap-2 pr-2">
                        {editingId === row.id ? (
                          <>
                            <Tooltip content="Guardar">
                              <Button
                                plain
                                icon={SaveMinor}
                                accessibilityLabel="Guardar alias"
                                onClick={handleUpdate}
                                disabled={!editCanonical || saving}
                              />
                            </Tooltip>
                            <Tooltip content="Cancelar">
                              <Button
                                plain
                                icon={CancelMinor}
                                accessibilityLabel="Cancelar edición"
                                onClick={() => setEditingId(null)}
                              />
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip content="Editar">
                              <Button
                                plain
                                icon={EditMinor}
                                accessibilityLabel="Editar alias"
                                onClick={() => startEdit(row)}
                              />
                            </Tooltip>
                            <Tooltip content="Borrar">
                              <Button
                                plain
                                destructive
                                icon={DeleteMinor}
                                accessibilityLabel="Borrar alias"
                                onClick={() => handleDelete(row)}
                                disabled={saving}
                              />
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </span>
                  )}
                </Row>
              </Fragment>
            ))
          )}
        </ul>
        {canEdit && (
          <Row canEdit={canEdit} className="py-4 border-none">
            <TextField
              // label="Código que llega"
              value={alias}
              onChange={setAlias}
              autoComplete="off"
              placeholder="EAN entrada"
            />
            <div>
              {/* <Text variant="bodySm" tone="subdued">
                Se resuelve a
              </Text> */}
              <Select options={variantOptions} value={canonical} onChange={setCanonical} />
            </div>
            <TextField
              // label="Motivo"
              value={reason}
              onChange={setReason}
              autoComplete="off"
              placeholder="EAN duplicado en EPH"
            />
            <Button onClick={handleCreate} disabled={!alias.trim() || !canonical || saving} loading={saving}>
              Añadir
            </Button>
          </Row>
        )}
      </LegacyCard.Section>
    </>
  );
};

export default ProductAliasesTab;
